import { useTranslation } from "react-i18next";

import type {
  CommercialScenarioInput,
  CommercialScenarioResult,
  CommercialValidationIssue,
} from "@/api/commercialCalibration";
import { formatBrl } from "@/components/projects/diagnostic/diagnosticData";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import { fieldIssues, numberOrNull, type CommercialForm } from "./form";

type NumericField = Exclude<keyof CommercialScenarioInput, "name" | "take_to_diagnostic">;

/**
 * Um cenário de venda (EF-FUNC-012 v0.1 RF-05 e RF-06, v0.3 RF-01 e RF-04).
 *
 * Margem alvo e uso de cada funding, as linhas que a prévia do servidor
 * calculou e a marca "Levar ao Diagnóstico". Cenário com campo fora da faixa
 * vem sem linhas da prévia, e as linhas ficam em traço.
 */
export function ScenarioCard({
  position,
  scenario,
  result,
  issues,
  onChange,
  onToggleDiagnostic,
}: {
  position: number;
  scenario: CommercialForm["scenarios"][number];
  result: CommercialScenarioResult | null | undefined;
  issues: CommercialValidationIssue[] | undefined;
  onChange: (patch: Partial<CommercialForm["scenarios"][number]>) => void;
  onToggleDiagnostic: () => void;
}) {
  const { t } = useTranslation();
  const id = (field: string) => `commercial-scenario-${position}-${field}`;
  const money = (value: number | undefined) => (value == null ? "—" : formatBrl(value));
  const nameMissing = !scenario.name.trim();
  const scenarioErrors = (issues ?? []).filter(
    (issue) => issue.scenario === position && issue.field == null,
  );
  const numeric: { field: NumericField; label: string; ariaLabel: string; max: string }[] = [
    {
      field: "hyperscale_use_pct",
      label: "Hyperscale",
      ariaLabel: t("{{funding}} use (%)", { funding: "Hyperscale" }),
      max: t("Between 0% and 100%."),
    },
    {
      field: "consider_selection_use_pct",
      label: "Consider Selection (C&S)",
      ariaLabel: t("{{funding}} use (%)", { funding: "Consider Selection (C&S)" }),
      max: t("Between 0% and 100%."),
    },
    {
      field: "adopt_operation_use_pct",
      label: "Adopt Operation (A&O)",
      ariaLabel: t("{{funding}} use (%)", { funding: "Adopt Operation (A&O)" }),
      max: t("Between 0% and 100%."),
    },
  ];

  //: As quatro linhas da MOC v3: o Incentivo Exed e o Funding Hyperscale saem do
  //: valor original em linhas separadas (v0.3 RF-04). Repasse e margem + incentivos
  //: continuam na resposta da API, fora da tela.
  const deduction = (value: number | undefined) => (value == null ? "—" : `− ${formatBrl(value)}`);
  const lines: { key: string; label: string; value: string; testId: string; tone?: string }[] = [
    {
      key: "total",
      label: t("Project total (original value)"),
      value: money(result?.project_total),
      testId: "scenario-project-total",
    },
    {
      key: "incentive",
      label: t("Exed incentive (C&S + A&O)"),
      value: deduction(result?.exed_incentive),
      testId: "scenario-exed-incentive",
      tone: "text-[#15803d]",
    },
    {
      key: "hyperscale",
      label: t("Hyperscale funding"),
      value: deduction(result?.hyperscale_funding),
      testId: "scenario-hyperscale-funding",
      tone: "text-[#9333ea]",
    },
    {
      key: "offered",
      label: t("Offered value to the client"),
      value: money(result?.offered_value),
      testId: "scenario-offered-value",
      tone: "font-semibold text-[#7a5400]",
    },
  ];

  return (
    <section
      data-testid={`commercial-scenario-${position}`}
      className={cn(
        "flex flex-col gap-2 rounded-xl border bg-white p-3.5",
        scenario.take_to_diagnostic ? "border-2 border-[#15803d] bg-[#f6fbf7]" : "border-[#e8ebf0]",
      )}
    >
      {/* MOC v3: o nome e, à direita, "Levar ao Diagnóstico". */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between gap-2">
          {/* O nome é editável, mas se apresenta como o título do cenário. */}
          <Input
            aria-label={t("Scenario name")}
            value={scenario.name}
            maxLength={60}
            aria-invalid={nameMissing}
            onChange={(event) => onChange({ name: event.target.value })}
            className={cn(
              "h-7 min-w-0 flex-1 border-transparent bg-transparent px-1 text-[15px] font-bold text-[#17233a] shadow-none hover:border-input focus-visible:border-input",
              nameMissing && "border-destructive",
            )}
          />
          <button
            type="button"
            role="radio"
            aria-checked={scenario.take_to_diagnostic}
            aria-label={t("Take to the Diagnostic")}
            onClick={onToggleDiagnostic}
            className="flex shrink-0 items-center gap-1.5 text-xs text-[#15803d]"
          >
            <span
              aria-hidden
              className="flex h-3.5 w-3.5 items-center justify-center rounded-full border border-[#15803d]"
            >
              {scenario.take_to_diagnostic ? (
                <span className="h-2 w-2 rounded-full bg-[#15803d]" />
              ) : null}
            </span>
            {t("Take to the Diagnostic")}
          </button>
        </div>
        {nameMissing ? (
          <p className="text-[11px] text-destructive">{t("Give the scenario a name.")}</p>
        ) : null}
      </div>

      <ScenarioInput
        id={id("target_margin_pct")}
        label={t("Target margin")}
        value={scenario.target_margin_pct}
        errors={fieldIssues(issues, "target_margin_pct", position)}
        message={t("Between 0% and 95%.")}
        onChange={(value) => onChange({ target_margin_pct: value })}
      />
      <p className="mt-1 text-[11.5px] text-[#8aa]">
        {t("Use of the fundings in this scenario")}
      </p>
      {numeric.map((row) => (
        <ScenarioInput
          key={row.field}
          id={id(row.field)}
          label={row.label}
          ariaLabel={row.ariaLabel}
          value={scenario[row.field]}
          errors={fieldIssues(issues, row.field, position)}
          message={row.max}
          onChange={(value) => onChange({ [row.field]: value })}
        />
      ))}

      <div className="mt-2 flex flex-col">
        {lines.map((line) => (
          <div
            key={line.key}
            className={cn(
              "flex items-center justify-between gap-2 py-1 text-[12.5px]",
              line.key === "offered"
                ? "mt-1 rounded-md bg-[#fff7e6] px-1.5 font-semibold text-[#7a5400]"
                : "border-b border-dashed border-[#eef1f6]",
            )}
          >
            <span className="text-[#596b85]">{line.label}</span>
            <span data-testid={line.testId} className={cn("font-mono tabular-nums", line.tone)}>
              {line.value}
            </span>
          </div>
        ))}
      </div>

      {scenarioErrors.map((issue) => (
        <p
          key={issue.code}
          data-testid="scenario-error"
          className="rounded-md border border-[#e8c4c4] bg-[#fdf4f4] px-2 py-1.5 text-[11px] text-[#8a3a3a]"
        >
          {t("The scenario taken to the Diagnostic needs an offered value above zero.")}
        </p>
      ))}
      {scenario.take_to_diagnostic ? (
        <span className="self-start rounded-full bg-[#e8f5ec] px-2.5 py-0.5 text-[11px] font-semibold text-[#15803d]">
          {t("Goes to the Diagnostic")}
        </span>
      ) : null}
    </section>
  );
}

function ScenarioInput({
  id,
  label,
  ariaLabel,
  value,
  errors,
  message,
  onChange,
}: {
  id: string;
  label: string;
  ariaLabel?: string;
  value: number | null;
  errors: CommercialValidationIssue[];
  message: string;
  onChange: (value: number | null) => void;
}) {
  const { t } = useTranslation();
  const empty = value === null;
  const invalid = empty || errors.length > 0;
  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center justify-between gap-2 text-[12.5px]">
        {/* Com `ariaLabel`, o rótulo visível é só texto: ligado ao campo, ele
            repetiria o nome do parâmetro de mesmo nome no quadro de cima. */}
        {ariaLabel ? (
          <span className="text-[#596b85]">{label}</span>
        ) : (
          <Label htmlFor={id} className="text-[12.5px] font-normal text-[#596b85]">
            {label}
          </Label>
        )}
        <div className="flex items-center gap-1">
          <Input
            id={id}
            aria-label={ariaLabel}
            type="number"
            value={value ?? ""}
            aria-invalid={invalid}
            onChange={(event) => onChange(numberOrNull(event.target.value))}
            className={cn("h-7 w-20 text-right tabular-nums", invalid && "border-destructive")}
          />
          <span className="text-[11px] text-muted-foreground">%</span>
        </div>
      </div>
      {invalid ? (
        <p className="text-right text-[11px] text-destructive">
          {empty ? t("Fill in the value.") : message}
        </p>
      ) : null}
    </div>
  );
}
